// src/components/student/StudentPromotion.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  InputAdornment,
  LinearProgress,
} from '@mui/material';
import {
  Preview as PreviewIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  PersonOff as PersonOffIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  SelectAll as SelectAllIcon,
  LockOutlined as LockIcon,
} from '@mui/icons-material';
import { academicAPI } from '../../services/api';
import { useStudentPromotion } from '../../hooks/useStudentPromotion';
import { usePermissions, RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import PermissionGuard from '../access-control/PermissionGuard';

// ─── helpers ────────────────────────────────────────────────────────────────

function classStreamSortKey(cs) {
  const level = cs.school_class?.level;
  if (level?.order != null) return Number(level.order);
  const fromLevelName = parseInt((level?.name || '').replace(/\D/g, ''), 10);
  if (!isNaN(fromLevelName)) return fromLevelName;
  const fromClassName = parseInt((cs.school_class?.name || '').replace(/\D/g, ''), 10);
  if (!isNaN(fromClassName)) return fromClassName;
  return 0;
}

function isHighestClass(cs, allClassStreams) {
  if (!allClassStreams.length) return false;
  const maxKey = Math.max(...allClassStreams.map(classStreamSortKey));
  return maxKey > 0 && classStreamSortKey(cs) === maxKey;
}

function csName(cs) {
  return cs?.display_name || `${cs?.school_class?.name}-${cs?.stream?.name}` || 'Unknown';
}

function applyFilters(rows, activeFilterId, search) {
  let result = rows;
  if (activeFilterId !== 'all') {
    result = result.filter((r) => String(r.from_class_stream_id) === activeFilterId);
  }
  if (search.trim()) {
    const q = search.trim().toLowerCase();
    result = result.filter(
      (r) =>
        r.full_name.toLowerCase().includes(q) ||
        (r.registration_no || '').toLowerCase().includes(q)
    );
  }
  return result;
}

// ─── component ──────────────────────────────────────────────────────────────

const StudentPromotion = () => {
  const {
    loading: promotionLoading,
    getStudentsForPromotion,
    executePromotion,
  } = useStudentPromotion();

  const [classStreams, setClassStreams]         = useState([]);
  const [promotionMap, setPromotionMap]         = useState({});
  const [allStudentCounts, setAllStudentCounts] = useState({});
  const [loading, setLoading]                   = useState(false);
  const [promoting, setPromoting]               = useState(false);

  // preview dialog
  const [showPreview, setShowPreview]           = useState(false);
  const [previewRows, setPreviewRows]           = useState([]);
  const [checkedIds, setCheckedIds]             = useState(new Set());
  const [activeFilterId, setActiveFilterId]     = useState('all');
  const [previewSearch, setPreviewSearch]       = useState('');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── bootstrap ─────────────────────────────────────────────────────────────

  useEffect(() => { fetchClassStreams(); }, []);

  const fetchClassStreams = async () => {
    try {
      setLoading(true);
      const response = await academicAPI.getClassStreams();
      const streams  = response.data || [];
      setClassStreams(streams);
      buildPromotionMap(streams);
      fetchAllStudentCounts(streams);
    } catch (error) {
      console.error('Error fetching class streams:', error);
      setSnackbar({ open: true, message: 'Failed to load class streams', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const buildPromotionMap = (streams) => {
    const map = {};
    streams.forEach((cs) => {
      const highest = isHighestClass(cs, streams);
      map[cs.id] = {
        classStream: cs,
        targetId: highest ? 'deactivate' : '',
        isHighest: highest,
      };
    });
    setPromotionMap(map);
  };

  const fetchAllStudentCounts = async (streams) => {
    const results = await Promise.allSettled(
      streams.map((cs) =>
        getStudentsForPromotion(cs.id).then((s) => ({ id: cs.id, count: s.length }))
      )
    );
    const counts = {};
    results.forEach((r) => { if (r.status === 'fulfilled') counts[r.value.id] = r.value.count; });
    setAllStudentCounts(counts);
  };

  // ── target change ──────────────────────────────────────────────────────────

  const handleTargetChange = (sourceId, newTargetId) => {
    setPromotionMap((prev) => ({
      ...prev,
      [sourceId]: { ...prev[sourceId], targetId: newTargetId },
    }));
  };

  // ── readiness check ────────────────────────────────────────────────────────
  // Preview is only enabled when EVERY class stream has a target set (not '').

  const entries            = Object.values(promotionMap);
  const totalStreams        = entries.length;
  const configuredStreams   = entries.filter((e) => e.targetId !== '');
  const missingTargets      = entries.filter((e) => e.targetId === '');
  const allConfigured       = totalStreams > 0 && missingTargets.length === 0;
  const configuredCount     = configuredStreams.length;

  // ── build preview rows ─────────────────────────────────────────────────────

  const buildPreviewRows = async () => {
    const rows = [];
    await Promise.all(
      configuredStreams.map(async (entry) => {
        const students     = await getStudentsForPromotion(entry.classStream.id);
        const isDeactivate = entry.targetId === 'deactivate';
        const targetCs     = isDeactivate ? null : classStreams.find((c) => c.id === Number(entry.targetId));

        students.forEach((student) => {
          rows.push({
            student_id:               student.id,
            full_name:                student.full_name,
            registration_no:          student.registration_no || student.lin,
            gender:                   student.gender,
            from_class_stream_id:     entry.classStream.id,
            from_class_stream_name:   csName(entry.classStream),
            target_class_stream_id:   isDeactivate ? null : Number(entry.targetId),
            target_class_stream_name: isDeactivate ? 'Deactivate (graduate)' : csName(targetCs),
            action:                   isDeactivate ? 'deactivate' : 'promote',
          });
        });
      })
    );
    return rows;
  };

  // ── open preview ───────────────────────────────────────────────────────────

  const handlePreview = async () => {
    try {
      setPromoting(true);
      const rows = await buildPreviewRows();
      setPreviewRows(rows);
      // All students checked by default
      setCheckedIds(new Set(rows.map((r) => r.student_id)));
      setActiveFilterId('all');
      setPreviewSearch('');
      setShowPreview(true);
    } catch (error) {
      console.error('Error building preview:', error);
      setSnackbar({ open: true, message: 'Failed to build preview', severity: 'error' });
    } finally {
      setPromoting(false);
    }
  };

  // ── checkbox handlers ──────────────────────────────────────────────────────

  const handleToggleStudent = (studentId) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      next.has(studentId) ? next.delete(studentId) : next.add(studentId);
      return next;
    });
  };

  const visibleIds = useMemo(
    () => applyFilters(previewRows, activeFilterId, previewSearch).map((r) => r.student_id),
    [previewRows, activeFilterId, previewSearch]
  );

  const allVisibleChecked  = visibleIds.length > 0 && visibleIds.every((id) => checkedIds.has(id));
  const someVisibleChecked = visibleIds.some((id) => checkedIds.has(id));

  const handleToggleAllVisible = () => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleChecked) visibleIds.forEach((id) => next.delete(id));
      else                   visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  // ── execute ────────────────────────────────────────────────────────────────

  const handleExecutePromotion = async () => {
    const toProcess = previewRows.filter((r) => checkedIds.has(r.student_id));
    if (toProcess.length === 0) {
      setSnackbar({ open: true, message: 'No students selected', severity: 'warning' });
      return;
    }

    const promotePayloads = toProcess
      .filter((r) => r.action === 'promote')
      .map((r) => ({ student_id: r.student_id, target_class_stream_id: r.target_class_stream_id, action: 'promote' }));

    const deactivatePayloads = toProcess
      .filter((r) => r.action === 'deactivate')
      .map((r) => ({ student_id: r.student_id, target_class_stream_id: null, action: 'deactivate' }));

    try {
      setPromoting(true);
      let successMsg = '';

      if (promotePayloads.length > 0) {
        const data  = await executePromotion(promotePayloads);
        successMsg += data.message || `${promotePayloads.length} student(s) promoted. `;
      }
      if (deactivatePayloads.length > 0) {
        const data  = await executePromotion(deactivatePayloads);
        successMsg += data.message || `${deactivatePayloads.length} student(s) deactivated. `;
      }

      setSnackbar({ open: true, message: successMsg.trim() || 'Promotion complete!', severity: 'success' });
      setShowPreview(false);
      buildPromotionMap(classStreams);
      fetchAllStudentCounts(classStreams);
    } catch (error) {
      console.error('Error executing promotion:', error);
      setSnackbar({ open: true, message: 'Failed to execute promotion', severity: 'error' });
    } finally {
      setPromoting(false);
    }
  };

  // ── derived values for preview dialog ─────────────────────────────────────

  const totalChecked = checkedIds.size;
  const totalRows    = previewRows.length;

  const previewStreamGroups = useMemo(() => {
    const map = {};
    previewRows.forEach((r) => {
      if (!map[r.from_class_stream_id]) {
        map[r.from_class_stream_id] = {
          id: r.from_class_stream_id,
          name: r.from_class_stream_name,
          action: r.action,
          total: 0,
          checked: 0,
        };
      }
      map[r.from_class_stream_id].total++;
      if (checkedIds.has(r.student_id)) map[r.from_class_stream_id].checked++;
    });
    return Object.values(map);
  }, [previewRows, checkedIds]);

  // ── render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>Loading class streams…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Student Promotion</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Set a target class stream for <strong>every</strong> class below, then preview and confirm
        all promotions in one step.
      </Typography>

      {/* Progress indicator */}
      {totalStreams > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="body2" color="text.secondary">
              {configuredCount} of {totalStreams} class streams configured
            </Typography>
            {!allConfigured && (
              <Typography variant="caption" color="warning.main" display="flex" alignItems="center" gap={0.5}>
                <LockIcon sx={{ fontSize: 13 }} />
                {missingTargets.length} stream{missingTargets.length !== 1 ? 's' : ''} still need a target
              </Typography>
            )}
            {allConfigured && (
              <Typography variant="caption" color="success.main" display="flex" alignItems="center" gap={0.5}>
                <CheckCircleIcon sx={{ fontSize: 13 }} />
                All streams configured — ready to preview
              </Typography>
            )}
          </Box>
          <LinearProgress
            variant="determinate"
            value={totalStreams > 0 ? (configuredCount / totalStreams) * 100 : 0}
            color={allConfigured ? 'success' : 'warning'}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
      )}

      {/* Mapping table */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Source class stream</TableCell>
                  <TableCell align="center">Active students</TableCell>
                  <TableCell>Target class stream</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {classStreams.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No class streams found</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {classStreams.map((cs) => {
                  const entry = promotionMap[cs.id];
                  if (!entry) return null;
                  const { isHighest, targetId } = entry;
                  const studentCount = allStudentCounts[cs.id];
                  const isMissing    = targetId === '';

                  return (
                    <TableRow
                      key={cs.id}
                      sx={{
                        backgroundColor: isMissing
                          ? 'error.50'
                          : isHighest
                          ? 'warning.50'
                          : 'success.50',
                      }}
                    >
                      {/* Source */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Chip
                            label={csName(cs)}
                            size="small"
                            color={isMissing ? 'error' : isHighest ? 'warning' : 'primary'}
                            variant="outlined"
                          />
                          {isHighest && (
                            <Tooltip title="Highest class — students will be deactivated (graduated) by default">
                              <WarningIcon fontSize="small" color="warning" />
                            </Tooltip>
                          )}
                          {isMissing && (
                            <Tooltip title="Target required before preview is available">
                              <LockIcon fontSize="small" color="error" />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      {/* Count */}
                      <TableCell align="center">
                        {studentCount == null
                          ? <CircularProgress size={14} />
                          : <Typography variant="body2" fontWeight="medium">{studentCount}</Typography>
                        }
                      </TableCell>

                      {/* Target selector */}
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 230 }}>
                          <Select
                            value={targetId}
                            onChange={(e) => handleTargetChange(cs.id, e.target.value)}
                            displayEmpty
                            error={isMissing}
                            sx={isMissing ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'error.main' } } : {}}
                          >
                            <MenuItem value="" disabled>
                              <Typography variant="body2" color="error.main">
                                — select a target —
                              </Typography>
                            </MenuItem>
                            <MenuItem value="deactivate">
                              <Box display="flex" alignItems="center" gap={1}>
                                <PersonOffIcon fontSize="small" color="warning" />
                                <Typography variant="body2" color="warning.main">
                                  Deactivate students (graduate)
                                </Typography>
                              </Box>
                            </MenuItem>
                            <Divider />
                            {classStreams
                              .filter((t) => t.id !== cs.id)
                              .map((t) => (
                                <MenuItem key={t.id} value={t.id}>{csName(t)}</MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </TableCell>

                      {/* Action badge */}
                      <TableCell align="center">
                        {isMissing && (
                          <Chip label="Needs target" size="small" color="error" variant="outlined" />
                        )}
                        {!isMissing && targetId === 'deactivate' && (
                          <Chip icon={<PersonOffIcon />} label="Deactivate" size="small" color="warning" />
                        )}
                        {!isMissing && targetId !== 'deactivate' && (
                          <Chip
                            icon={<TrendingUpIcon />}
                            label={`→ ${csName(classStreams.find((c) => c.id === Number(targetId)))}`}
                            size="small"
                            color="success"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Preview button — locked until all streams configured */}
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
        {!allConfigured && (
          <Typography variant="body2" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
            <LockIcon sx={{ fontSize: 15 }} />
            Set targets for all {missingTargets.length} remaining stream{missingTargets.length !== 1 ? 's' : ''} to unlock preview
          </Typography>
        )}
        <Tooltip
          title={!allConfigured ? `${missingTargets.length} class stream${missingTargets.length !== 1 ? 's' : ''} still need a target` : ''}
          placement="top"
        >
          <span>
            <Button
              variant="contained"
              startIcon={promoting ? <CircularProgress size={16} color="inherit" /> : allConfigured ? <PreviewIcon /> : <LockIcon />}
              onClick={handlePreview}
              disabled={promoting || !allConfigured}
              color={allConfigured ? 'primary' : 'inherit'}
              size="large"
            >
              {promoting ? 'Loading…' : `Preview All Classes (${totalStreams})`}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* ── Preview Dialog ──────────────────────────────────────────────────── */}
      <Dialog
        open={showPreview}
        onClose={() => !promoting && setShowPreview(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}
      >
        {/* Title + live summary */}
        <DialogTitle sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Promotion Preview</Typography>
            <IconButton onClick={() => setShowPreview(false)} size="small" disabled={promoting}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" rowGap={1}>
            <Chip
              icon={<CheckCircleIcon />}
              label={`${totalChecked} / ${totalRows} selected`}
              color="primary"
              size="small"
            />
            <Chip
              icon={<TrendingUpIcon />}
              label={`${previewRows.filter((r) => r.action === 'promote' && checkedIds.has(r.student_id)).length} to promote`}
              color="success"
              size="small"
              variant="outlined"
            />
            <Chip
              icon={<PersonOffIcon />}
              label={`${previewRows.filter((r) => r.action === 'deactivate' && checkedIds.has(r.student_id)).length} to deactivate`}
              color="warning"
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${totalRows - totalChecked} stay unchanged`}
              size="small"
              variant="outlined"
            />
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', pt: 2 }}>

          {/* Class-stream filter chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} mb={1.5}>
            <Chip
              label={`All (${totalRows})`}
              onClick={() => setActiveFilterId('all')}
              color={activeFilterId === 'all' ? 'primary' : 'default'}
              variant={activeFilterId === 'all' ? 'filled' : 'outlined'}
              size="small"
              clickable
            />
            {previewStreamGroups.map((group) => (
              <Chip
                key={group.id}
                icon={
                  group.action === 'deactivate'
                    ? <PersonOffIcon style={{ fontSize: 14 }} />
                    : <TrendingUpIcon style={{ fontSize: 14 }} />
                }
                label={`${group.name} (${group.checked}/${group.total})`}
                onClick={() => setActiveFilterId(String(group.id))}
                color={
                  activeFilterId === String(group.id)
                    ? group.action === 'deactivate' ? 'warning' : 'success'
                    : 'default'
                }
                variant={activeFilterId === String(group.id) ? 'filled' : 'outlined'}
                size="small"
                clickable
              />
            ))}
          </Stack>

          {/* Search + select-all */}
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <TextField
              size="small"
              placeholder="Search by name or registration…"
              value={previewSearch}
              onChange={(e) => setPreviewSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, maxWidth: 340 }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<SelectAllIcon />}
              onClick={handleToggleAllVisible}
              disabled={visibleIds.length === 0}
            >
              {allVisibleChecked ? 'Deselect visible' : 'Select visible'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {visibleIds.length} shown
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 1.5 }} icon={<InfoIcon fontSize="small" />}>
            <Typography variant="body2">
              <strong>Unchecked students stay in their current class stream</strong> — they are neither
              promoted nor deactivated.
            </Typography>
          </Alert>

          {/* Student table */}
          <TableContainer component={Paper} elevation={0} sx={{ flex: 1, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 48 }}>
                    <Checkbox
                      indeterminate={someVisibleChecked && !allVisibleChecked}
                      checked={allVisibleChecked}
                      onChange={handleToggleAllVisible}
                      size="small"
                      disabled={visibleIds.length === 0}
                    />
                  </TableCell>
                  <TableCell>Student</TableCell>
                  <TableCell>Reg. No</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To / Action</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applyFilters(previewRows, activeFilterId, previewSearch).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No students match the current filter
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {applyFilters(previewRows, activeFilterId, previewSearch).map((row) => {
                  const isChecked    = checkedIds.has(row.student_id);
                  const isDeactivate = row.action === 'deactivate';

                  return (
                    <TableRow
                      key={row.student_id}
                      hover
                      onClick={() => handleToggleStudent(row.student_id)}
                      sx={{
                        cursor: 'pointer',
                        opacity: isChecked ? 1 : 0.4,
                        backgroundColor: !isChecked
                          ? 'action.hover'
                          : isDeactivate
                          ? 'warning.50'
                          : 'success.50',
                        transition: 'opacity 0.15s, background-color 0.15s',
                      }}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleToggleStudent(row.student_id)}
                          size="small"
                          color={isDeactivate ? 'warning' : 'success'}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight={isChecked ? 'medium' : 'regular'}>
                          {row.full_name}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {row.registration_no}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={row.gender}
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'capitalize', fontSize: 11 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip label={row.from_class_stream_name} size="small" variant="outlined" />
                      </TableCell>

                      <TableCell>
                        {isChecked ? (
                          isDeactivate ? (
                            <Chip icon={<PersonOffIcon />} label="Deactivate (graduate)" size="small" color="warning" />
                          ) : (
                            <Chip icon={<TrendingUpIcon />} label={`→ ${row.target_class_stream_name}`} size="small" color="success" />
                          )
                        ) : (
                          <Chip label="Stays in current class" size="small" variant="outlined" />
                        )}
                      </TableCell>

                      <TableCell align="center">
                        {isChecked ? (
                          <CheckCircleIcon fontSize="small" color={isDeactivate ? 'warning' : 'success'} />
                        ) : (
                          <Tooltip title="Will not be moved or deactivated">
                            <WarningIcon fontSize="small" color="disabled" />
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {totalChecked} student{totalChecked !== 1 ? 's' : ''} will be acted upon &bull;{' '}
            {totalRows - totalChecked} will stay unchanged
          </Typography>
          <Button onClick={() => setShowPreview(false)} disabled={promoting}>
            Cancel
          </Button>
          <PermissionGuard resource={RESOURCES.STUDENTS} action={ACTIONS.UPDATE}>
            <Button
              variant="contained"
              startIcon={promoting ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleExecutePromotion}
              disabled={promoting || totalChecked === 0}
              color="success"
            >
              {promoting ? 'Processing…' : `Confirm & Execute (${totalChecked})`}
            </Button>
          </PermissionGuard>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StudentPromotion;